import './styles.css';
import { openCropEditor } from './crop';
import { FfmpegExporter } from './export/ffmpeg';
import type { SceneBase } from './export/frames';
import { DividerSampler, pathTimeline, sampledTimeline, sweepTimeline, withTailHold } from './export/timeline';
import { ExportCancelledError, type ExportProgress, type ExportResult, type ExportSegment, type Timeline } from './export/types';
import { createFullscreenStage } from './fullscreen';
import { getCropRect } from './geometry';
import { canvasToThumb, loadImageFile } from './images';
import { attachInput } from './input';
import { logoFromDataUrl, prepareLogoFile } from './logo';
import { buildSmoothPath, samplePath, type PathSample, type SmoothPath } from './path';
import { drawScene, type Watermark } from './render';
import {
  ASPECTS,
  DEFAULT_SCALE,
  MAX_SETS,
  activeSet,
  anySetComplete,
  completeSets,
  defaultTransform,
  hasBothImages,
  newPhotoSet,
  state,
  type AspectKey,
  type Direction,
  type ExportMode,
  type LoadedImage,
  type PhotoSet,
  type Side,
} from './state';
import {
  appendConsentEntry,
  clearConsentLog,
  consentLogToCsv,
  loadConsentLog,
  loadLogoDataUrl,
  loadSettings,
  newEntryId,
  saveLogoDataUrl,
  saveSettings,
  sha256Hex,
  type BrandingCorner,
  type ConsentEntry,
  type Settings,
} from './storage';

// ---------------------------------------------------------------------------
// DOM
// ---------------------------------------------------------------------------

function $<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Missing element #${id}`);
  return el as T;
}

const angleTabsEl = $('angleTabs');
const fileInputs: Record<Side, HTMLInputElement> = { A: $('fileA'), B: $('fileB') };
const drops: Record<Side, HTMLElement> = { A: $('dropA'), B: $('dropB') };
const debugMsg = $('debugMsg');
const canvas = $<HTMLCanvasElement>('canvas');
const canvasHolder = $('canvasHolder');
const emptyNote = $('emptyNote');
const controls = $('controls');
const recordSection = $('recordSection');
const aspectSel = $<HTMLSelectElement>('aspect');
const directionSel = $<HTMLSelectElement>('direction');
const showLabels = $<HTMLInputElement>('showLabels');
const disclaimerEnabled = $<HTMLInputElement>('disclaimerEnabled');
const disclaimerText = $<HTMLInputElement>('disclaimerText');
const brandingEnabled = $<HTMLInputElement>('brandingEnabled');
const brandingText = $<HTMLInputElement>('brandingText');
const brandingCorner = $<HTMLSelectElement>('brandingCorner');
const brandingScale = $<HTMLInputElement>('brandingScale');
const brandingScaleVal = $('brandingScaleVal');
const logoSlot = $('logoSlot');
const logoFile = $<HTMLInputElement>('logoFile');
const logoHint = $('logoHint');
const logoRemoveBtn = $<HTMLButtonElement>('logoRemoveBtn');
const durationInput = $<HTMLInputElement>('duration');
const durVal = $('durVal');
const recordBtn = $<HTMLButtonElement>('recordBtn');
const startManualBtn = $<HTMLButtonElement>('startManualBtn');
const stopManualBtn = $<HTMLButtonElement>('stopManualBtn');
const exportModeBtns = Array.from(document.querySelectorAll<HTMLButtonElement>('#exportModeBtns button'));
const autoPanel = $('autoPanel');
const manualPanel = $('manualPanel');
const customPanel = $('customPanel');
const stillPanel = $('stillPanel');
const downloadStillBtn = $<HTMLButtonElement>('downloadStillBtn');
const recordPathBtn = $<HTMLButtonElement>('recordPathBtn');
const stopPathBtn = $<HTMLButtonElement>('stopPathBtn');
const pathStatus = $('pathStatus');
const pathActions = $('pathActions');
const previewPathBtn = $<HTMLButtonElement>('previewPathBtn');
const exportPathBtn = $<HTMLButtonElement>('exportPathBtn');
const clearPathBtn = $<HTMLButtonElement>('clearPathBtn');
const flipBtn = $<HTMLButtonElement>('flipBtn');
const resetBtn = $<HTMLButtonElement>('resetBtn');
const statusEl = $('status');
const exportProgress = $('exportProgress');
const exportPhase = $('exportPhase');
const exportBar = $<HTMLProgressElement>('exportBar');
const cancelExportBtn = $<HTMLButtonElement>('cancelExportBtn');
const downloadWrap = $('downloadWrap');
const downloadWrapLabel = $('downloadWrapLabel');
const downloadLink = $<HTMLAnchorElement>('downloadLink');
const previewVideo = $<HTMLVideoElement>('previewVideo');
const previewImage = $<HTMLImageElement>('previewImage');
const shareBtn = $<HTMLButtonElement>('shareBtn');
const alignPanel = $('alignPanel');
const zoomSliders: Record<Side, HTMLInputElement> = { A: $('zoomASlider'), B: $('zoomBSlider') };
const resetAlignBtns: Record<Side, HTMLButtonElement> = { A: $('resetAlignABtn'), B: $('resetAlignBBtn') };
const lockBoxes: Record<Side, HTMLInputElement> = { A: $('lockA'), B: $('lockB') };
const guideToggle = $<HTMLInputElement>('guideToggle');
const nudgeBtns = Array.from(document.querySelectorAll<HTMLButtonElement>('.nudge'));
const consentBlock = $('consentBlock');
const consentCheck = $<HTMLInputElement>('consentCheck');
const consentRef = $<HTMLInputElement>('consentRef');
const consentLogCount = $('consentLogCount');
const consentLogBody = $<HTMLTableSectionElement>('consentLogBody');
const consentLogEmpty = $('consentLogEmpty');
const downloadLogBtn = $<HTMLButtonElement>('downloadLogBtn');
const clearLogBtn = $<HTMLButtonElement>('clearLogBtn');
const cropStage = $('cropStage');
const cropCanvas = $<HTMLCanvasElement>('cropCanvas');
const cropResetBtn = $<HTMLButtonElement>('cropResetBtn');
const cropCancelBtn = $<HTMLButtonElement>('cropCancelBtn');
const cropApplyBtn = $<HTMLButtonElement>('cropApplyBtn');

const fullscreen = createFullscreenStage({
  canvas,
  holder: canvasHolder,
  stage: $('fullscreenStage'),
  stopButton: $('fsStopBtn'),
});

const ctx = canvas.getContext('2d');
if (!ctx) throw new Error('2D canvas context unavailable');

const SIDES: readonly Side[] = ['A', 'B'];
const PLACEHOLDER: Record<Side, string> = { A: 'Before (left)', B: 'After (right)' };

/** Manual recordings stop themselves here so the encode stays bounded. */
const MAX_MANUAL_MS = 60_000;
/** Beat held on the final frame of each angle in an auto/custom export. */
const END_HOLD_MS = 150;

// ---------------------------------------------------------------------------
// Error surface (kept from the prototype: mobile Safari has no console)
// ---------------------------------------------------------------------------

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function showDebug(msg: string): void {
  debugMsg.hidden = false;
  debugMsg.textContent = msg;
}

function hideDebug(): void {
  debugMsg.hidden = true;
  debugMsg.textContent = '';
}

window.addEventListener('error', (e) => {
  const err: unknown = e.error;
  const detail = err instanceof Error ? `${err.name}: ${err.message}` : e.message || 'unknown';
  const where = e.filename ? ` @ ${e.filename.split('/').pop()}:${e.lineno}:${e.colno}` : '';
  const stack = err instanceof Error && err.stack ? `\n${err.stack.split('\n').slice(0, 3).join('\n')}` : '';
  showDebug('Error: ' + detail + where + stack);
});
// Resource load failures (script, image, video) do not bubble; catch them in the capture phase.
window.addEventListener(
  'error',
  (e) => {
    const t = e.target;
    if (t instanceof Element) {
      const src = (t as HTMLImageElement).src || (t as HTMLLinkElement).href || '';
      showDebug(`Failed to load <${t.tagName.toLowerCase()}> ${src}`);
    }
  },
  true,
);
window.addEventListener('unhandledrejection', (e) => {
  showDebug('Promise error: ' + errMsg(e.reason));
});

// ---------------------------------------------------------------------------
// Persisted settings (disclaimer caption)
// ---------------------------------------------------------------------------

let settings: Settings = loadSettings();
disclaimerEnabled.checked = settings.disclaimerEnabled;
disclaimerText.value = settings.disclaimerText;

/** The caption to burn into frames right now, or null when off/blank. */
function currentDisclaimer(): string | null {
  const text = settings.disclaimerText.trim();
  return settings.disclaimerEnabled && text ? text : null;
}

disclaimerEnabled.addEventListener('change', () => {
  settings = { ...settings, disclaimerEnabled: disclaimerEnabled.checked };
  saveSettings(settings);
  draw();
});
disclaimerText.addEventListener('input', () => {
  settings = { ...settings, disclaimerText: disclaimerText.value };
  saveSettings(settings);
  draw();
});

// ---------------------------------------------------------------------------
// Branding watermark (text handle + optional logo), saved on the device
// ---------------------------------------------------------------------------

/** Decoded logo ready to draw, or null. */
let logoCanvas: HTMLCanvasElement | null = null;

brandingEnabled.checked = settings.brandingEnabled;
brandingText.value = settings.brandingText;
brandingCorner.value = settings.brandingCorner;
brandingScale.value = String(Math.round(settings.brandingLogoScale * 100));
brandingScaleVal.textContent = `${brandingScale.value}%`;

function currentWatermark(): Watermark | null {
  if (!settings.brandingEnabled) return null;
  const text = settings.brandingText.trim() || null;
  if (!text && !logoCanvas) return null;
  return { text, logo: logoCanvas, corner: settings.brandingCorner, logoScale: settings.brandingLogoScale };
}

/** Short description of the branding applied, for the consent log. */
function brandingSummary(): string | null {
  const wm = currentWatermark();
  if (!wm) return null;
  return [wm.text, wm.logo ? 'logo' : null].filter(Boolean).join(' + ');
}

function renderLogoSlot(): void {
  logoSlot.replaceChildren();
  if (logoCanvas) {
    const img = document.createElement('img');
    img.src = logoCanvas.toDataURL('image/png');
    img.alt = 'Logo';
    logoSlot.append(img);
    logoHint.textContent = 'Tap the logo to replace it.';
    logoRemoveBtn.hidden = false;
  } else {
    const tag = document.createElement('span');
    tag.className = 'tag';
    tag.textContent = '+ Logo';
    logoSlot.append(tag);
    logoHint.textContent = 'Optional. A PNG with a transparent background looks best.';
    logoRemoveBtn.hidden = true;
  }
}

brandingEnabled.addEventListener('change', () => {
  settings = { ...settings, brandingEnabled: brandingEnabled.checked };
  saveSettings(settings);
  draw();
});
brandingText.addEventListener('input', () => {
  settings = { ...settings, brandingText: brandingText.value };
  saveSettings(settings);
  draw();
});
brandingCorner.addEventListener('change', () => {
  settings = { ...settings, brandingCorner: brandingCorner.value as BrandingCorner };
  saveSettings(settings);
  draw();
});
brandingScale.addEventListener('input', () => {
  brandingScaleVal.textContent = `${brandingScale.value}%`;
  settings = { ...settings, brandingLogoScale: parseInt(brandingScale.value, 10) / 100 };
  saveSettings(settings);
  draw();
});

logoSlot.addEventListener('click', () => logoFile.click());
logoSlot.addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    logoFile.click();
  }
});
logoFile.addEventListener('change', () => {
  const f = logoFile.files?.[0];
  logoFile.value = '';
  if (!f) return;
  prepareLogoFile(f)
    .then(({ canvas: c, dataUrl }) => {
      logoCanvas = c;
      if (!saveLogoDataUrl(dataUrl)) setStatus('The logo could not be saved in this browser (storage blocked); it will apply until you reload.');
      renderLogoSlot();
      draw();
    })
    .catch((err: unknown) => showDebug('Logo: ' + errMsg(err)));
});
logoRemoveBtn.addEventListener('click', () => {
  logoCanvas = null;
  saveLogoDataUrl(null);
  renderLogoSlot();
  draw();
});

renderLogoSlot();
{
  const stored = loadLogoDataUrl();
  if (stored) {
    logoFromDataUrl(stored)
      .then((c) => {
        logoCanvas = c;
        renderLogoSlot();
        draw();
      })
      .catch(() => saveLogoDataUrl(null));
  }
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function draw(): void {
  const set = activeSet();
  const imgA = set.images.A;
  const imgB = set.images.B;
  if (!imgA || !imgB) return;
  drawScene(ctx!, {
    imgA: imgA.bitmap,
    imgB: imgB.bitmap,
    transformA: set.transforms.A,
    transformB: set.transforms.B,
    sliderX: state.sliderX,
    // The face guide is an alignment aid only. It is never part of an export,
    // including the fullscreen playback a screen recording might capture.
    showGuide: state.showGuide && !state.recording,
    showLabels: state.showLabels,
    disclaimer: currentDisclaimer(),
    watermark: currentWatermark(),
  });
}

/** Immutable copy of everything an export needs for one angle, except the divider position. */
function sceneForSet(set: PhotoSet): SceneBase {
  const imgA = set.images.A;
  const imgB = set.images.B;
  if (!imgA || !imgB) throw new Error('Both images are required');
  return {
    imgA: imgA.bitmap,
    imgB: imgB.bitmap,
    transformA: { ...set.transforms.A },
    transformB: { ...set.transforms.B },
    showGuide: false,
    showLabels: state.showLabels,
    disclaimer: currentDisclaimer(),
    watermark: currentWatermark(),
  };
}

function sceneSnapshot(): SceneBase {
  return sceneForSet(activeSet());
}

function resizeCanvas(): void {
  const [w, h] = ASPECTS[state.aspect];
  canvas.width = w;
  canvas.height = h;
  state.sliderX = w / 2;
  draw();
}

/** Show/hide the stage and export section for whichever set is now active, and (re)size the canvas. */
function syncStageForActiveSet(): void {
  const ready = hasBothImages();
  if (ready) {
    resizeCanvas();
    preloadEncoder();
  } else {
    // Nothing to preview for this angle yet; clear any previous angle's pixels
    // rather than leaving them behind the "upload both images" note.
    ctx!.clearRect(0, 0, canvas.width, canvas.height);
  }
  emptyNote.hidden = ready;
  controls.hidden = !ready;
  alignPanel.hidden = !ready;
  recordSection.hidden = !anySetComplete();
}

// ---------------------------------------------------------------------------
// Angle tabs (multi-angle sets)
// ---------------------------------------------------------------------------

function renderAngleTabs(): void {
  angleTabsEl.replaceChildren();
  angleTabsEl.classList.toggle('busy', state.recording || state.pathRecording);

  if (state.sets.length === 1) {
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'angle-add-link';
    add.textContent = '+ Add another angle';
    add.addEventListener('click', addAngle);
    angleTabsEl.append(add);
    return;
  }

  state.sets.forEach((set, i) => {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = 'angle-tab' + (i === state.activeSetIndex ? ' active' : '');
    tab.addEventListener('click', () => switchActiveSet(i));

    const label = document.createElement('span');
    label.textContent = set.label;
    tab.append(label);

    const remove = document.createElement('span');
    remove.className = 'remove';
    remove.textContent = '×';
    remove.setAttribute('role', 'button');
    remove.setAttribute('aria-label', `Remove ${set.label}`);
    remove.addEventListener('click', (e) => {
      e.stopPropagation();
      removeAngle(i);
    });
    tab.append(remove);

    angleTabsEl.append(tab);
  });

  if (state.sets.length < MAX_SETS) {
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'angle-add';
    add.textContent = '+';
    add.setAttribute('aria-label', 'Add another angle');
    add.addEventListener('click', addAngle);
    angleTabsEl.append(add);
  }
}

/** Sync every control that shows the active set's own data, after switching, adding, or removing one. */
function refreshActiveSetUI(): void {
  renderDrop('A');
  renderDrop('B');
  syncZoomSliders();
  const set = activeSet();
  lockBoxes.A.checked = set.locked.A;
  lockBoxes.B.checked = set.locked.B;
  syncLockUI();
  syncStageForActiveSet();
  renderAngleTabs();
  syncExportGate();
}

function switchActiveSet(i: number): void {
  if (i === state.activeSetIndex) return;
  state.activeSetIndex = i;
  refreshActiveSetUI();
}

function addAngle(): void {
  if (state.sets.length >= MAX_SETS) return;
  state.sets.push(newPhotoSet(`Angle ${state.sets.length + 1}`));
  state.activeSetIndex = state.sets.length - 1;
  refreshActiveSetUI();
}

function removeAngle(i: number): void {
  if (state.sets.length <= 1) return;
  const label = state.sets[i]!.label;
  if (!confirm(`Remove ${label}? Its photos will be discarded.`)) return;
  state.sets.splice(i, 1);
  state.sets.forEach((s, idx) => {
    s.label = `Angle ${idx + 1}`;
  });
  state.activeSetIndex = Math.min(state.activeSetIndex, state.sets.length - 1);
  refreshActiveSetUI();
}

// ---------------------------------------------------------------------------
// Image upload slots
// ---------------------------------------------------------------------------

function renderDrop(side: Side): void {
  const drop = drops[side];
  const img = activeSet().images[side];
  drop.replaceChildren();
  if (img) {
    drop.classList.add('filled');
    const thumb = document.createElement('img');
    thumb.src = img.thumb;
    thumb.alt = '';
    const cropBtn = document.createElement('button');
    cropBtn.type = 'button';
    cropBtn.className = 'crop-btn';
    cropBtn.textContent = 'Crop';
    cropBtn.setAttribute('aria-label', `Crop the ${PLACEHOLDER[side].toLowerCase()} photo`);
    cropBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      void startCrop(side);
    });
    const name = document.createElement('div');
    name.className = 'filename';
    name.textContent = `${img.name} · tap to replace`;
    drop.append(thumb, cropBtn, name);
  } else {
    drop.classList.remove('filled');
    const plus = document.createElement('span');
    plus.className = 'plus';
    plus.textContent = '+';
    const tag = document.createElement('span');
    tag.className = 'tag';
    tag.textContent = PLACEHOLDER[side];
    drop.append(plus, tag);
  }
}

/**
 * Open the crop tool for one photo of the active set and apply the result.
 * Replaces that side's bitmap and thumbnail, resets its pan/zoom (the old
 * offsets were measured against the pre-crop dimensions), and requires
 * consent to be reconfirmed, same as any other photo change.
 */
async function startCrop(side: Side): Promise<void> {
  const set = activeSet();
  const img = set.images[side];
  if (!img) return;
  const result = await openCropEditor(img.bitmap, {
    stage: cropStage,
    canvas: cropCanvas,
    resetBtn: cropResetBtn,
    cancelBtn: cropCancelBtn,
    applyBtn: cropApplyBtn,
  });
  if (!result) return;
  set.images[side] = { bitmap: result, thumb: canvasToThumb(result), name: img.name };
  set.transforms[side] = defaultTransform();
  zoomSliders[side].value = String(Math.round(DEFAULT_SCALE * 100));
  renderDrop(side);
  resetConsent();
  syncStageForActiveSet();
  draw();
}

function setImage(side: Side, img: LoadedImage): void {
  const set = activeSet();
  set.images[side] = img;
  set.transforms[side] = defaultTransform();
  zoomSliders[side].value = String(Math.round(DEFAULT_SCALE * 100));
  renderDrop(side);
  // Consent is per patient: a new or replaced photo needs a fresh confirmation.
  resetConsent();
  syncStageForActiveSet();
  renderAngleTabs();
  draw();
}

for (const side of SIDES) {
  const input = fileInputs[side];
  const drop = drops[side];
  renderDrop(side);
  drop.addEventListener('click', () => input.click());
  drop.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      input.click();
    }
  });
  input.addEventListener('change', () => {
    const f = input.files?.[0];
    // reset so re-choosing the same file fires `change` again
    input.value = '';
    if (!f) return;
    loadImageFile(f)
      .then((img) => setImage(side, img))
      .catch((err: unknown) => showDebug(errMsg(err)));
  });
}

// ---------------------------------------------------------------------------
// Format controls
// ---------------------------------------------------------------------------

state.aspect = aspectSel.value as AspectKey;
state.direction = directionSel.value as Direction;
state.showLabels = showLabels.checked;
state.showGuide = guideToggle.checked;

aspectSel.addEventListener('change', () => {
  state.aspect = aspectSel.value as AspectKey;
  if (hasBothImages()) resizeCanvas();
});
directionSel.addEventListener('change', () => {
  state.direction = directionSel.value as Direction;
});
showLabels.addEventListener('change', () => {
  state.showLabels = showLabels.checked;
  draw();
});
guideToggle.addEventListener('change', () => {
  state.showGuide = guideToggle.checked;
  draw();
});

// ---------------------------------------------------------------------------
// Alignment controls: lock, zoom, reset, nudge — all act on the active set
// ---------------------------------------------------------------------------

function syncLockUI(): void {
  const set = activeSet();
  for (const side of SIDES) {
    const locked = set.locked[side];
    zoomSliders[side].disabled = locked;
    resetAlignBtns[side].disabled = locked;
  }
  for (const btn of nudgeBtns) {
    btn.disabled = set.locked[btn.dataset.target as Side];
  }
}

function syncZoomSliders(): void {
  const set = activeSet();
  for (const side of SIDES) {
    zoomSliders[side].value = String(Math.round(set.transforms[side].scale * 100));
  }
}

for (const side of SIDES) {
  lockBoxes[side].addEventListener('change', () => {
    activeSet().locked[side] = lockBoxes[side].checked;
    syncLockUI();
  });

  zoomSliders[side].addEventListener('input', () => {
    if (activeSet().locked[side]) return;
    activeSet().transforms[side].scale = parseFloat(zoomSliders[side].value) / 100;
    draw();
  });

  resetAlignBtns[side].addEventListener('click', () => {
    if (activeSet().locked[side]) return;
    activeSet().transforms[side] = defaultTransform();
    zoomSliders[side].value = String(Math.round(DEFAULT_SCALE * 100));
    draw();
  });
}

/**
 * Move a photo by 1% of its currently visible crop per tap. The button
 * arrow must match the direction the image visually moves: pressing ▶
 * shifts the crop window left in source space, so the image slides right.
 */
function nudge(side: Side, dx: number, dy: number): void {
  const set = activeSet();
  if (set.locked[side]) return;
  const img = set.images[side];
  if (!img || !hasBothImages()) return;
  const t = set.transforms[side];
  const { sw, sh } = getCropRect(img.bitmap, canvas.width, canvas.height, t);
  const step = 0.01;
  t.offsetX -= dx * sw * step;
  t.offsetY -= dy * sh * step;
  draw();
}

for (const btn of nudgeBtns) {
  btn.addEventListener('click', () => {
    nudge(btn.dataset.target as Side, parseFloat(btn.dataset.dx ?? '0'), parseFloat(btn.dataset.dy ?? '0'));
  });
}

flipBtn.addEventListener('click', () => {
  const set = activeSet();
  const { A, B } = set.images;
  set.images = { A: B, B: A };
  set.transforms = { A: set.transforms.B, B: set.transforms.A };
  set.locked = { A: set.locked.B, B: set.locked.A };
  lockBoxes.A.checked = set.locked.A;
  lockBoxes.B.checked = set.locked.B;
  syncLockUI();
  syncZoomSliders();
  renderDrop('A');
  renderDrop('B');
  draw();
});

resetBtn.addEventListener('click', () => {
  state.sliderX = canvas.width / 2;
  draw();
});

// ---------------------------------------------------------------------------
// Canvas input (drag divider / pan / pinch) — always the active set
// ---------------------------------------------------------------------------

let pathSamples: PathSample[] = [];
let pathStartTs = 0;
let smoothedPath: SmoothPath | null = null;

attachInput(canvas, {
  draw,
  onSliderMove(x) {
    if (state.pathRecording) pathSamples.push({ t: performance.now() - pathStartTs, x });
  },
  onScaleChanged(side, scale) {
    zoomSliders[side].value = String(Math.round(scale * 100));
  },
});

// ---------------------------------------------------------------------------
// Consent gate + log
// ---------------------------------------------------------------------------

/** When the consent box was ticked for the current photo(s). */
let consentConfirmedAt: string | null = null;

function consentGiven(): boolean {
  return consentCheck.checked && consentConfirmedAt !== null;
}

/** Export triggers are only enabled with consent confirmed, no export running, and something to export. */
function syncExportGate(): void {
  const notBusy = !state.recording && !state.pathRecording;
  const consentOk = consentGiven();
  recordBtn.disabled = !(consentOk && notBusy && anySetComplete());
  startManualBtn.disabled = !(consentOk && notBusy && hasBothImages());
  exportPathBtn.disabled = !(consentOk && notBusy && hasBothImages() && smoothedPath !== null && !state.autoAnimating);
  downloadStillBtn.disabled = !(consentOk && notBusy && hasBothImages());
  consentBlock.classList.toggle('consent-missing', !consentOk);
}

function resetConsent(): void {
  consentCheck.checked = false;
  consentConfirmedAt = null;
  consentRef.value = '';
  syncExportGate();
}

consentCheck.addEventListener('change', () => {
  consentConfirmedAt = consentCheck.checked ? new Date().toISOString() : null;
  syncExportGate();
});

function renderConsentLog(): void {
  const entries = loadConsentLog();
  consentLogCount.textContent = String(entries.length);
  consentLogEmpty.hidden = entries.length > 0;
  downloadLogBtn.disabled = entries.length === 0;
  clearLogBtn.disabled = entries.length === 0;
  const rows = entries
    .slice()
    .reverse()
    .slice(0, 100)
    .map((e) => {
      const tr = document.createElement('tr');
      const when = document.createElement('td');
      when.textContent = new Date(e.exportedAt).toLocaleString();
      const ref = document.createElement('td');
      ref.textContent = e.reference || '—';
      const what = document.createElement('td');
      const angles = e.angles > 1 ? `${e.angles} angles · ` : '';
      what.textContent =
        e.mode === 'still' ? `still · ${e.aspect}` : `${e.mode} · ${e.aspect} · ${angles}${(e.durationMs / 1000).toFixed(1)}s`;
      const fp = document.createElement('td');
      fp.className = 'fp';
      fp.textContent = e.sha256.slice(0, 16);
      fp.title = e.sha256;
      tr.append(when, ref, what, fp);
      return tr;
    });
  consentLogBody.replaceChildren(...rows);
}

async function logExport(result: ExportResult, mode: ExportMode, durationMs: number, angles: number): Promise<void> {
  const entry: ConsentEntry = {
    id: newEntryId(),
    exportedAt: new Date().toISOString(),
    consentConfirmedAt: consentConfirmedAt ?? '',
    reference: consentRef.value.trim(),
    mode,
    aspect: state.aspect,
    durationMs: Math.round(durationMs),
    fileBytes: result.blob.size,
    sha256: await sha256Hex(result.blob),
    disclaimer: currentDisclaimer(),
    branding: brandingSummary(),
    angles,
  };
  if (!appendConsentEntry(entry)) {
    setStatus(statusEl.textContent + ' Note: the consent log could not be saved in this browser (storage blocked).');
  }
  renderConsentLog();
}

downloadLogBtn.addEventListener('click', () => {
  const csv = consentLogToCsv(loadConsentLog());
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `consent-log-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
});

clearLogBtn.addEventListener('click', () => {
  if (!confirm('Delete every consent log entry stored on this device? This cannot be undone. Download the log first if you need a copy.')) return;
  clearConsentLog();
  renderConsentLog();
});

// ---------------------------------------------------------------------------
// Busy-state UI locking
// ---------------------------------------------------------------------------

function lockUI(busy: boolean): void {
  aspectSel.disabled = busy;
  flipBtn.disabled = busy;
  resetBtn.disabled = busy;
  guideToggle.disabled = busy;
  disclaimerEnabled.disabled = busy;
  disclaimerText.disabled = busy;
  brandingEnabled.disabled = busy;
  brandingText.disabled = busy;
  brandingCorner.disabled = busy;
  brandingScale.disabled = busy;
  logoRemoveBtn.disabled = busy;
  logoSlot.classList.toggle('busy', busy);
  consentCheck.disabled = busy;
  consentRef.disabled = busy;
  angleTabsEl.classList.toggle('busy', busy);
  for (const side of SIDES) {
    lockBoxes[side].disabled = busy;
    drops[side].classList.toggle('busy', busy);
  }
  for (const b of exportModeBtns) b.disabled = busy;
  recordPathBtn.disabled = busy;
  previewPathBtn.disabled = busy;

  if (busy) {
    recordBtn.disabled = true;
    startManualBtn.disabled = true;
    exportPathBtn.disabled = true;
    downloadStillBtn.disabled = true;
    for (const side of SIDES) {
      zoomSliders[side].disabled = true;
      resetAlignBtns[side].disabled = true;
    }
    for (const b of nudgeBtns) b.disabled = true;
  } else {
    syncLockUI(); // restores each side's own lock state instead of force-enabling
    syncExportGate();
  }
}

function setStatus(msg: string): void {
  statusEl.textContent = msg;
}

// ---------------------------------------------------------------------------
// Export mode switcher
// ---------------------------------------------------------------------------

function setExportMode(mode: ExportMode): void {
  for (const b of exportModeBtns) b.classList.toggle('active', b.dataset.exportmode === mode);
  autoPanel.hidden = mode !== 'auto';
  manualPanel.hidden = mode !== 'manual';
  customPanel.hidden = mode !== 'custom';
  stillPanel.hidden = mode !== 'still';
}

for (const b of exportModeBtns) {
  b.addEventListener('click', () => setExportMode(b.dataset.exportmode as ExportMode));
}

durationInput.addEventListener('input', () => {
  durVal.textContent = parseFloat(durationInput.value).toFixed(1) + 's';
});

// ---------------------------------------------------------------------------
// Encoder (ffmpeg.wasm) lifecycle + progress UI
// ---------------------------------------------------------------------------

const exporter = new FfmpegExporter();
let exportAbort: AbortController | null = null;

const PREPARING_PREFIX = 'Downloading the video encoder in the background';
const preparingMsg = (pct: number): string => `${PREPARING_PREFIX} (one-time, about 31 MB)… ${pct}%`;

function preloadEncoder(): void {
  if (exporter.ready) return;
  const ownsStatus = (): boolean => statusEl.textContent === '' || statusEl.textContent!.startsWith(PREPARING_PREFIX);
  if (ownsStatus()) setStatus(preparingMsg(0));
  exporter
    .preload((loaded, total) => {
      if (ownsStatus()) setStatus(preparingMsg(Math.round((100 * loaded) / total)));
    })
    .then(() => {
      if (ownsStatus()) setStatus('Video encoder ready.');
    })
    .catch(() => {
      // Surfaced with a real message when an export is attempted.
      if (ownsStatus()) setStatus('');
    });
}

function renderProgress(p: ExportProgress): void {
  exportBar.value = p.fraction;
  switch (p.phase) {
    case 'loading':
      exportPhase.textContent = `Downloading video encoder (one-time, about 31 MB)… ${Math.round(p.fraction * 100)}%`;
      break;
    case 'rendering':
      exportPhase.textContent = `Rendering frame ${p.rendered} of ${p.total}`;
      break;
    case 'encoding':
      exportPhase.textContent = `Encoding video… ${p.encoded} of ${p.total} frames`;
      break;
    case 'finalizing':
      exportPhase.textContent = 'Finalizing MP4…';
      break;
  }
}

cancelExportBtn.addEventListener('click', () => {
  exportAbort?.abort();
  cancelExportBtn.disabled = true;
  exportPhase.textContent = 'Cancelling…';
});

// ---------------------------------------------------------------------------
// Delivery of a finished export
// ---------------------------------------------------------------------------

let lastVideoUrl: string | null = null;

function deliverResult(result: ExportResult): void {
  const { blob, mimeType, ext } = result;
  const isImage = mimeType.startsWith('image/');

  if (lastVideoUrl) URL.revokeObjectURL(lastVideoUrl);
  const url = URL.createObjectURL(blob);
  lastVideoUrl = url;

  previewVideo.hidden = isImage;
  previewImage.hidden = !isImage;
  if (isImage) previewImage.src = url;
  else previewVideo.src = url;

  downloadWrapLabel.textContent = isImage
    ? 'Image ready — tap Save / Share to send it to Photos, or use the download link:'
    : 'Video ready — tap Save / Share to send it to Photos, or use the download link:';
  downloadLink.href = url;
  downloadLink.download = 'before-after.' + ext;
  downloadLink.textContent = isImage ? 'Download image' : 'Download video';
  downloadWrap.hidden = false;

  const fileObj = new File([blob], 'before-after.' + ext, { type: mimeType });
  if (navigator.canShare && navigator.canShare({ files: [fileObj] })) {
    shareBtn.hidden = false;
    shareBtn.onclick = async () => {
      try {
        await navigator.share({ files: [fileObj], title: 'Before / After' });
      } catch {
        // cancelled or unsupported at call time, no action needed
      }
    };
  } else {
    shareBtn.hidden = true;
  }

  const kb = Math.round(blob.size / 1024);
  const kind = isImage ? 'PNG' : 'MP4';
  const savedAs = isImage ? 'Save Image' : 'Save Video';
  setStatus(`Done — ${kb} KB ${kind}. On iPhone, tap Save / Share and choose ${savedAs}. On desktop, use the download link.`);
}

// ---------------------------------------------------------------------------
// Export orchestration
// ---------------------------------------------------------------------------

function beginExport(): void {
  state.recording = true;
  lockUI(true);
  hideDebug();
  downloadWrap.hidden = true;
}

function endExport(): void {
  state.recording = false;
  state.autoAnimating = false;
  lockUI(false);
  draw(); // restore the live view (e.g. the guide, hidden during export)
}

/** Drive the live canvas through a timeline in real time (fullscreen playback). */
function playTimeline(timeline: Timeline): Promise<void> {
  return new Promise((resolve) => {
    const t0 = performance.now();
    const step = (now: number): void => {
      const t = Math.min(now - t0, timeline.durationMs);
      state.sliderX = timeline.xAt(t);
      draw();
      if (t < timeline.durationMs) requestAnimationFrame(step);
      else setTimeout(resolve, END_HOLD_MS);
    };
    requestAnimationFrame(step);
  });
}

/** Render + encode one or more angle segments offline, then show the result. Handles cancel/errors. */
async function encodeAndDeliver(segments: ExportSegment<SceneBase>[], mode: ExportMode, angles: number): Promise<void> {
  const controller = new AbortController();
  exportAbort = controller;
  cancelExportBtn.disabled = false;
  exportBar.value = 0;
  exportPhase.textContent = 'Preparing…';
  exportProgress.hidden = false;
  setStatus('');

  const totalDurationMs = segments.reduce((sum, s) => sum + s.timeline.durationMs, 0);
  try {
    const result = await exporter.export({
      segments,
      width: canvas.width,
      height: canvas.height,
      signal: controller.signal,
      onProgress: renderProgress,
    });
    deliverResult(result);
    await logExport(result, mode, totalDurationMs, angles);
  } catch (err) {
    if (err instanceof ExportCancelledError) {
      setStatus('Export cancelled.');
    } else {
      showDebug('Video export failed: ' + errMsg(err));
      setStatus(
        'Export failed on this device. As a fallback, use your phone’s screen recorder while the fullscreen playback runs.',
      );
    }
  } finally {
    exportAbort = null;
    exportProgress.hidden = true;
    endExport();
  }
}

/** Auto sweep (custom pattern shares this) for a single, already-active set: play it fullscreen, then encode. */
async function playThenEncode(timeline: Timeline, mode: ExportMode): Promise<void> {
  if (!hasBothImages() || state.recording) return;
  if (!consentGiven()) {
    setStatus('Confirm patient consent above before exporting.');
    return;
  }
  beginExport();
  state.autoAnimating = true;
  fullscreen.enter(false);
  await playTimeline(timeline);
  state.autoAnimating = false;
  fullscreen.exit();
  await encodeAndDeliver([{ scene: sceneSnapshot(), timeline: withTailHold(timeline, END_HOLD_MS) }], mode, 1);
}

/**
 * Auto sweep across one or more angles: play each angle's sweep fullscreen
 * in turn (so a screen-recording fallback still captures the whole thing),
 * then encode all of them into one MP4. A single complete set behaves
 * exactly like the single-angle path.
 */
async function playMultiAngleSweep(sets: PhotoSet[]): Promise<void> {
  if (sets.length === 0 || state.recording) return;
  if (!consentGiven()) {
    setStatus('Confirm patient consent above before exporting.');
    return;
  }
  beginExport();
  state.autoAnimating = true;
  fullscreen.enter(false);
  const savedActiveIndex = state.activeSetIndex;
  const durationMs = parseFloat(durationInput.value) * 1000;
  const w = canvas.width;
  const startX = state.direction === 'ltr' ? 0 : w;
  const endX = state.direction === 'ltr' ? w : 0;

  const segments: ExportSegment<SceneBase>[] = [];
  for (const set of sets) {
    state.activeSetIndex = state.sets.indexOf(set);
    const timeline = sweepTimeline(durationMs, startX, endX);
    await playTimeline(timeline);
    segments.push({ scene: sceneForSet(set), timeline: withTailHold(timeline, END_HOLD_MS) });
  }
  state.activeSetIndex = savedActiveIndex;
  state.autoAnimating = false;
  fullscreen.exit();
  await encodeAndDeliver(segments, 'auto', sets.length);
}

recordBtn.addEventListener('click', () => {
  const sets = completeSets();
  if (sets.length === 0) {
    setStatus('Upload both photos for at least one angle before exporting.');
    return;
  }
  void playMultiAngleSweep(sets);
});

// ---------------------------------------------------------------------------
// Manual drag: sample the divider every frame while the user drags, then encode
// (always the active angle only — a live multi-angle drag doesn't map cleanly
// onto one continuous gesture)
// ---------------------------------------------------------------------------

const sampler = new DividerSampler();

startManualBtn.addEventListener('click', () => {
  if (!hasBothImages() || state.recording) return;
  if (!consentGiven()) {
    setStatus('Confirm patient consent above before exporting.');
    return;
  }
  beginExport();
  fullscreen.enter(true);
  startManualBtn.hidden = true;
  stopManualBtn.hidden = false;
  setStatus('Recording — drag the slider now. Stops on its own after 60 seconds.');
  sampler.start(() => state.sliderX, { maxMs: MAX_MANUAL_MS, onMax: stopManualRecording });
});

function stopManualRecording(): void {
  if (!sampler.running) return;
  const samples = sampler.stop();
  stopManualBtn.hidden = true;
  startManualBtn.hidden = false;
  fullscreen.exit();
  const timeline = sampledTimeline(samples);
  if (!timeline) {
    setStatus('That recording was too short to use.');
    endExport();
    return;
  }
  void encodeAndDeliver([{ scene: sceneSnapshot(), timeline }], 'manual', 1);
}
stopManualBtn.addEventListener('click', stopManualRecording);
$('fsStopBtn').addEventListener('click', stopManualRecording);

// ---------------------------------------------------------------------------
// Custom pattern: capture a drag once, smooth it, preview/export the replay
// (always the active angle only, same reasoning as manual drag)
// ---------------------------------------------------------------------------

recordPathBtn.addEventListener('click', () => {
  if (!hasBothImages() || state.recording) return;
  pathSamples = [{ t: 0, x: state.sliderX }];
  pathStartTs = performance.now();
  state.pathRecording = true;
  smoothedPath = null;
  lockUI(true);
  recordPathBtn.hidden = true;
  stopPathBtn.hidden = false;
  pathActions.hidden = true;
  pathStatus.textContent = 'Recording your pattern — drag the handle now.';
});

stopPathBtn.addEventListener('click', () => {
  state.pathRecording = false;
  stopPathBtn.hidden = true;
  recordPathBtn.hidden = false;
  smoothedPath = buildSmoothPath(pathSamples);
  lockUI(false);
  if (!smoothedPath) {
    pathStatus.textContent = 'That was too short to use — try dragging for at least a second.';
    pathActions.hidden = true;
    return;
  }
  pathStatus.textContent = 'Captured ' + (smoothedPath.duration / 1000).toFixed(1) + 's. Preview it or export.';
  pathActions.hidden = false;
});

clearPathBtn.addEventListener('click', () => {
  smoothedPath = null;
  pathSamples = [];
  pathActions.hidden = true;
  pathStatus.textContent = '';
  syncExportGate();
});

previewPathBtn.addEventListener('click', () => {
  const path = smoothedPath;
  if (!path || state.autoAnimating || state.recording) return;
  state.autoAnimating = true;
  previewPathBtn.disabled = true;
  exportPathBtn.disabled = true;
  const t0 = performance.now();
  const step = (now: number): void => {
    const t = Math.min(now - t0, path.duration);
    state.sliderX = samplePath(path, t);
    draw();
    if (t < path.duration) {
      requestAnimationFrame(step);
    } else {
      state.autoAnimating = false;
      previewPathBtn.disabled = false;
      syncExportGate();
    }
  };
  requestAnimationFrame(step);
});

exportPathBtn.addEventListener('click', () => {
  const path = smoothedPath;
  if (!path) return;
  void playThenEncode(pathTimeline(path), 'custom');
});

// ---------------------------------------------------------------------------
// Still image: one frame at the current divider position, no video encoding
// needed. Useful for a print handout, a website gallery, or a single feed
// post — and centering the divider first gives the classic merged
// half-before/half-after look for free, since that's just what the compare
// view already renders at sliderX = width/2.
// ---------------------------------------------------------------------------

async function downloadStillImage(): Promise<void> {
  if (!hasBothImages() || state.recording) return;
  if (!consentGiven()) {
    setStatus('Confirm patient consent above before exporting.');
    return;
  }
  beginExport();
  try {
    const off = document.createElement('canvas');
    off.width = canvas.width;
    off.height = canvas.height;
    const octx = off.getContext('2d');
    if (!octx) throw new Error('2D canvas context unavailable');
    // Same scene the live preview shows (labels, disclaimer, branding),
    // minus the alignment guide, at whichever divider position is set now.
    drawScene(octx, { ...sceneSnapshot(), sliderX: state.sliderX });
    const blob = await new Promise<Blob | null>((resolve) => off.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('Could not create the image (canvas.toBlob returned null)');
    const result: ExportResult = { blob, mimeType: 'image/png', ext: 'png' };
    deliverResult(result);
    await logExport(result, 'still', 0, 1);
  } catch (err) {
    showDebug('Still image failed: ' + errMsg(err));
    setStatus('Could not create the image on this device.');
  } finally {
    endExport();
  }
}

downloadStillBtn.addEventListener('click', () => void downloadStillImage());

// ---------------------------------------------------------------------------
// Initial UI state
// ---------------------------------------------------------------------------

setExportMode('auto');
syncLockUI();
syncExportGate();
renderConsentLog();
renderAngleTabs();
